import { useState, useEffect } from "react";
import { SortingState } from "@tanstack/react-table";
import {
  getRequestsPaginated,
  type CredentialRequestFilters,
  type PaginatedResponse,
} from "@/data/credentialReqRepository";
import type { CredentialRequest, CredentialRequestStatus } from "@/models/credentialRequest";
import { convertBoliviaDateToUTCStart, convertBoliviaDateToUTCEnd } from "@/lib/dateUtils";

interface UseCredentialRequestListParams {
  statusFilter: string;
  districtFilter: string;
  regionFilter: string;
  dateFromFilter: string;
  dateToFilter: string;
  searchValue: string;
  sorting: SortingState;
  currentPage: number;
  pageSize: number;
  credentialType?: string;
  institutionId?: string;
}

export function useCredentialRequestList({
  statusFilter,
  districtFilter,
  regionFilter,
  dateFromFilter,
  dateToFilter,
  searchValue,
  sorting,
  currentPage,
  pageSize,
  credentialType,
  institutionId,
}: UseCredentialRequestListParams) {
  const [requests, setRequests] = useState<CredentialRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    const loadRequests = async () => {
      setIsLoading(true);
      try {
        const filters: CredentialRequestFilters = {};

        if (statusFilter) filters.status = statusFilter as CredentialRequestStatus;
        if (districtFilter) filters.districtId = districtFilter;
        if (regionFilter) filters.regionId = regionFilter;
        
        if (credentialType) filters.credentialType = credentialType;
        if (institutionId) filters.institutionId = institutionId;
        
        // Convert Bolivia dates to UTC timestamps
        if (dateFromFilter) {
          filters.dateFrom = convertBoliviaDateToUTCStart(dateFromFilter);
        }
        if (dateToFilter) {
          filters.dateTo = convertBoliviaDateToUTCEnd(dateToFilter);
        }
        
        if (searchValue) filters.search = searchValue;

        const sort =
          sorting.length > 0
            ? {
                field: sorting[0].id,
                direction: sorting[0].desc ? ("desc" as const) : ("asc" as const),
              }
            : undefined;

        const response: PaginatedResponse<CredentialRequest> = await getRequestsPaginated(
          currentPage,
          pageSize,
          filters,
          sort
        );

        setRequests(response.data);
        setTotalPages(response.totalPages);
        setTotalItems(response.count);
      } catch (error) {
        console.error("Error loading credential requests:", error);
        setRequests([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadRequests();
  }, [
    currentPage,
    pageSize,
    searchValue,
    sorting,
    statusFilter,
    districtFilter,
    regionFilter,
    dateFromFilter,
    dateToFilter,
    credentialType,
    institutionId,
  ]);

  const reloadRequests = async () => {
    const filters: CredentialRequestFilters = {};

    if (statusFilter) filters.status = statusFilter as CredentialRequestStatus;
    if (districtFilter) filters.districtId = districtFilter;
    if (regionFilter) filters.regionId = regionFilter;
    
    // Convert Bolivia dates to UTC timestamps
    if (dateFromFilter) {
      filters.dateFrom = convertBoliviaDateToUTCStart(dateFromFilter);
    }
    if (dateToFilter) {
      filters.dateTo = convertBoliviaDateToUTCEnd(dateToFilter);
    }
    
    if (searchValue) filters.search = searchValue;
    if (credentialType) filters.credentialType = credentialType;
    if (institutionId) filters.institutionId = institutionId;

    const sort =
      sorting.length > 0
        ? {
            field: sorting[0].id,
            direction: sorting[0].desc ? ("desc" as const) : ("asc" as const),
          }
        : undefined;

    try {
      const response = await getRequestsPaginated(currentPage, pageSize, filters, sort);
      setRequests(response.data);
      setTotalPages(response.totalPages);
      setTotalItems(response.count);
    } catch (error) {
      console.error("Error reloading requests:", error);
    }
  };

  return {
    requests,
    isLoading,
    totalPages,
    totalItems,
    reloadRequests,
  };
}
